/**
 * useWorkspaceRole — Normalize 3 auth backends into a single WorkspaceRole.
 *
 * Auth backends (tried in order):
 *   1. Clerk useUser — for admin, analyst, data_specialist, accountant_internal,
 *      AND attorney (Clerk-only cutover, 2026-07-22: an active Clerk
 *      Organization means this session belongs to an attorney — same
 *      `orgId` convention as `ProtectedAttorneyRoute` in
 *      AttorneyAuthContext.tsx; internal staff never have an active org).
 *   2. AttorneyAuthContext — legacy fallback, now inert (`token`/`attorney`
 *      are always null post-cutover; kept only in case a future local
 *      session type is added here).
 *   3. AccountantAuthContext — for accountant_external (JWT, implicit role)
 *
 * The hook extracts and normalizes role strings from whichever backend is active.
 * Returns null if no auth context is active or role cannot be determined.
 *
 * Usage:
 *   const role = useWorkspaceRole(); // WorkspaceRole | null
 *   if (role === null) { ... } // unauthenticated or unknown role
 */

import { useUser, useAuth } from '@clerk/clerk-react';
import { useAttorneyAuth } from '../contexts/AttorneyAuthContext';
import { useAccountantAuth } from '../contexts/AccountantAuthContext';
import type { WorkspaceRole } from '../config/toolsManifest';
import { isSeatRoleSotEnabled } from '../config/seatRoles';

// Valid WorkspaceRole literals from toolsManifest.ts
// 'admin' | 'analyst' | 'data_specialist' | 'accountant_internal' | 'accountant_external' | 'attorney' | 'client'
const VALID_ROLES: WorkspaceRole[] = [
  'admin',
  'analyst',
  'data_specialist',
  'accountant_internal',
  'accountant_external',
  'attorney',
  'attorney_internal',  // must precede the partial 'attorney' fallback
  'client',
  'mediator',  // Phase 7: external neutral (org_type='mediator')
  'public_adjuster',   // PR1 (2026-11-04)
  'carrier_adjuster',  // PR1 (2026-11-04)
];

/**
 * Normalize a raw role string to a valid WorkspaceRole literal.
 * Handles common variations and edge cases.
 */
function normalizeRole(raw: string | null | undefined): WorkspaceRole | null {
  if (!raw) return null;

  const normalized = raw.toLowerCase().trim().replace(/[-\s]/g, '_');

  // Direct match
  if (VALID_ROLES.includes(normalized as WorkspaceRole)) {
    return normalized as WorkspaceRole;
  }

  // Common aliases and variations
  const aliasMap: Record<string, WorkspaceRole> = {
    // Admin variations
    administrator: 'admin',
    superuser: 'admin',
    super_user: 'admin',
    staff: 'admin',
    // The platform owner passes every admin gate. Without this alias 'owner'
    // normalized to null and the owner account fell back to 'analyst' —
    // locking the owner out of every RoleRoute(['admin']) page (found
    // 2026-08-12 when /fleet redirected the owner to /dashboard).
    owner: 'admin',

    // Analyst variations
    analysis: 'analyst',

    // Data specialist variations
    data_specialists: 'data_specialist',
    data_special: 'data_specialist',
    dataspecialist: 'data_specialist',
    specialist: 'data_specialist',

    // Internal accountant variations
    accountant: 'accountant_internal',
    internal_accountant: 'accountant_internal',
    staff_accountant: 'accountant_internal',
    in_house_accountant: 'accountant_internal',

    // External accountant variations
    external_accountant: 'accountant_external',
    cpa: 'accountant_external',
    external: 'accountant_external',

    // Attorney variations
    lawyer: 'attorney',
    counsel: 'attorney',
    esq: 'attorney',

    // On-staff cross-firm reviewer
    internal_attorney: 'attorney_internal',

    // Client variations
    customer: 'client',
    insured: 'client',
    claimant: 'client',
  };

  if (aliasMap[normalized]) {
    return aliasMap[normalized];
  }

  // Partial matches for compound strings
  if (normalized.includes('admin')) return 'admin';
  if (normalized.includes('analyst')) return 'analyst';
  if (normalized.includes('data') && normalized.includes('special')) return 'data_specialist';
  if (normalized.includes('external') && normalized.includes('account')) return 'accountant_external';
  if (normalized.includes('internal') && normalized.includes('account')) return 'accountant_internal';
  if (normalized.includes('account') && !normalized.includes('external')) return 'accountant_internal';
  if (normalized.includes('attorney') || normalized.includes('lawyer')) return 'attorney';

  return null;
}

/**
 * Extract role from Clerk user metadata.
 * Clerk users store role in publicMetadata or unsafeMetadata.
 * seatRoleSot (REACT_APP_SEAT_ROLE_SOT, per-seat logins Phase 7, D8): never read
 * unsafeMetadata — the signed-in user can write it from the browser.
 */
function extractClerkRole(
  user: ReturnType<typeof useUser>['user'],
  seatRoleSot: boolean,
): WorkspaceRole | null {
  if (!user) return null;

  // Try publicMetadata first (recommended by Clerk)
  const publicRole = user.publicMetadata?.role as string | undefined;
  if (publicRole) {
    const normalized = normalizeRole(publicRole);
    if (normalized) return normalized;
  }

  // Fallback to unsafeMetadata (flag off only)
  if (!seatRoleSot) {
    const unsafeRole = user.unsafeMetadata?.role as string | undefined;
    if (unsafeRole) {
      const normalized = normalizeRole(unsafeRole);
      if (normalized) return normalized;
    }
  }

  // Try organization membership role
  const orgRole = user.organizationMemberships?.[0]?.role as string | undefined;
  if (orgRole) {
    const normalized = normalizeRole(orgRole);
    if (normalized) return normalized;
  }

  return null;
}

/**
 * React hook that returns the current WorkspaceRole by trying each auth backend.
 *
 * Tries backends in order:
 *   1. Clerk useUser — returns role from metadata if present
 *   2. AttorneyAuthContext — returns 'attorney' if authenticated
 *   3. AccountantAuthContext — returns 'accountant_external' if authenticated
 *
 * @returns WorkspaceRole | null — null means no auth active or role unknown
 */
export function useWorkspaceRole(): WorkspaceRole | null {
  // Try Clerk first (for staff/internal users, and attorneys via Organizations)
  const { user, isLoaded } = useUser();
  const { orgId } = useAuth();

  if (isLoaded && user) {
    // An EXPLICIT metadata role wins over the org signal (owner decision
    // 2026-08-12): internal staff/owner accounts carry publicMetadata.role,
    // and an active Organization on such a session (e.g. the owner opening
    // a firm workspace) must not demote them to 'attorney'. Real attorneys
    // never have a metadata role set, so this changes nothing for them.
    const clerkRole = extractClerkRole(user, isSeatRoleSotEnabled());
    if (clerkRole) {
      return clerkRole;
    }

    // Clerk Organizations cutover (2026-07-22): an active firm Organization
    // with no explicit metadata role means this Clerk session belongs to an
    // attorney — attorneys don't reliably have publicMetadata.role/
    // unsafeMetadata.role set.
    if (orgId) {
      return 'attorney';
    }

    // If no explicit role in metadata, default to 'analyst' for authenticated Clerk users
    // This handles the common case where internal staff don't have explicit role set
    return 'analyst';
  }

  // Legacy attorney JWT context — now inert (token/attorney are always null
  // post-Clerk-cutover). Kept as a harmless fallback; never returns 'attorney'
  // in practice anymore, since real attorneys are caught by the orgId check
  // above.
  try {
    const attorneyAuth = useAttorneyAuth();
    if (attorneyAuth.token && attorneyAuth.attorney) {
      return 'attorney';
    }
  } catch {
    // AttorneyAuthProvider not in tree — ignore
  }

  // Try External Accountant JWT context
  try {
    const accountantAuth = useAccountantAuth();
    if (accountantAuth.token && accountantAuth.accountant) {
      return 'accountant_external';
    }
  } catch {
    // AccountantAuthProvider not in tree — ignore
  }

  // No auth backend active
  return null;
}

export default useWorkspaceRole;
